import React, { useState, useEffect } from 'react';
import { Modal, Card, Row, Col, Badge, Spinner, Alert, Accordion, Button, Tabs, Tab } from 'react-bootstrap';
import { formatDate } from '../utils/dataUtils';
import { apiService } from '../services/api';
import BrandDetail from './BrandDetail';

const CategoryDetail = ({ show, onHide, categoryId, categoryName }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryDetails, setCategoryDetails] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [showBrandDetail, setShowBrandDetail] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    let mounted = true;

    const fetchDetails = async () => {
      if (!categoryId) return;
      
      try {
        setLoading(true);
        const details = await apiService.fetchCategoryDetails(categoryId, categoryName);
        if (mounted) {
          setCategoryDetails(details);
          setError(null);
        }
      } catch (err) {
        if (mounted) {
          setError(`Failed to load details for ${categoryName}`);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    if (show && categoryId) {
      fetchDetails();
    }

    return () => {
      mounted = false;
    };
  }, [show, categoryId, categoryName]);

  // Calculate totals from brands data
  const calculateTotals = (brands) => {
    return brands?.reduce(
      (acc, brand) => {
        acc.totalPosts += brand.brandpost;
        acc.totalComments += brand.comments;
        acc.totalAccounts += brand.totalaccounts;
        return acc;
      },
      { totalPosts: 0, totalComments: 0, totalAccounts: 0 }
    );
  };

  const totals = categoryDetails?.brands ? calculateTotals(categoryDetails.brands) : null;

  const handleBrandClick = (brand) => {
    setSelectedBrand(brand);
    setShowBrandDetail(true);
  };

  const handleCloseBrandDetail = () => {
    setShowBrandDetail(false);
  };

  // Render charts for the Charts tab
  const renderCharts = () => {
    if (!categoryDetails?.brands) return null;

    // Extract data for charts
    const brandNames = categoryDetails.brands.map(brand => brand.brand_name);
    const brandPosts = categoryDetails.brands.map(brand => brand.brandpost);
    const brandComments = categoryDetails.brands.map(brand => brand.comments);
    const brandAccounts = categoryDetails.brands.map(brand => brand.totalaccounts);

    return (
      <div className="charts-container">
        {/* Posts by Brand Chart */}
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Header className="bg-transparent">
            <h5 className="mb-0">Posts by Brand</h5>
          </Card.Header>
          <Card.Body>
            <div className="chart-wrapper bar-chart">
              <div className="chart-labels">
                {brandNames.map((name, i) => (
                  <div key={`label-${i}`} className="chart-label" title={name}>
                    {name.length > 15 ? `${name.substring(0, 12)}...` : name}
                  </div>
                ))}
              </div>
              <div className="chart-bars">
                {brandPosts.map((posts, i) => {
                  const maxPosts = Math.max(...brandPosts);
                  const percentage = (posts / maxPosts) * 100;
                  return (
                    <div key={`bar-${i}`} className="chart-bar-container">
                      <div 
                        className="chart-bar posts-bar" 
                        style={{ height: `${percentage}%` }}
                        title={`${brandNames[i]}: ${posts.toLocaleString()} posts`}
                      >
                        <span className="chart-value">{posts.toLocaleString()}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Comments by Brand Chart */}
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Header className="bg-transparent">
            <h5 className="mb-0">Comments by Brand</h5>
          </Card.Header>
          <Card.Body>
            <div className="chart-wrapper bar-chart">
              <div className="chart-labels">
                {brandNames.map((name, i) => (
                  <div key={`comment-label-${i}`} className="chart-label" title={name}>
                    {name.length > 15 ? `${name.substring(0, 12)}...` : name}
                  </div>
                ))}
              </div>
              <div className="chart-bars">
                {brandComments.map((comments, i) => {
                  const maxComments = Math.max(...brandComments);
                  const percentage = (comments / maxComments) * 100;
                  return (
                    <div key={`comment-bar-${i}`} className="chart-bar-container">
                      <div 
                        className="chart-bar comments-bar" 
                        style={{ height: `${percentage}%` }}
                        title={`${brandNames[i]}: ${comments.toLocaleString()} comments`}
                      >
                        <span className="chart-value">{comments.toLocaleString()}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card.Body>
        </Card>
        
        {/* Accounts Distribution Pie Chart */}
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Header className="bg-transparent">
            <h5 className="mb-0">Accounts Distribution</h5>
          </Card.Header>
          <Card.Body>
            <div className="pie-chart-container">
              <div className="pie-chart">
                {brandAccounts.map((accounts, index) => {
                  const totalAccounts = brandAccounts.reduce((sum, count) => sum + count, 0);
                  const percentage = (accounts / totalAccounts) * 100;
                  const pieColors = ['#4285F4', '#EA4335', '#FBBC05', '#34A853', '#8E24AA', '#16A2D7', '#FB8C00'];
                  const offset = brandAccounts.slice(0, index).reduce((sum, count) => {
                    return sum + (count / totalAccounts) * 100;
                  }, 0);
                  
                  return (
                    <div 
                      key={`pie-segment-${index}`}
                      className="pie-segment"
                      style={{
                        backgroundColor: pieColors[index % pieColors.length],
                        clipPath: `conic-gradient(from 0deg, currentColor ${offset}%, currentColor ${offset + percentage}%, transparent ${offset + percentage}%, transparent 100%)`
                      }}
                      title={`${brandNames[index]}: ${accounts} accounts (${percentage.toFixed(1)}%)`}
                    ></div>
                  );
                })}
              </div>
              <div className="pie-legend">
                {brandNames.map((name, index) => {
                  const pieColors = ['#4285F4', '#EA4335', '#FBBC05', '#34A853', '#8E24AA', '#16A2D7', '#FB8C00'];
                  const totalAccounts = brandAccounts.reduce((sum, count) => sum + count, 0);
                  const percentage = ((brandAccounts[index] / totalAccounts) * 100).toFixed(1);
                  
                  return (
                    <div key={`legend-${index}`} className="legend-item">
                      <div 
                        className="legend-color" 
                        style={{ backgroundColor: pieColors[index % pieColors.length] }}
                      ></div>
                      <div className="legend-text">
                        {name.length > 20 ? `${name.substring(0, 17)}...` : name}: {brandAccounts[index]} ({percentage}%)
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  };

  return (
    <>
      <Modal 
        show={show} 
        onHide={onHide} 
        size="xl" 
        centered
        className="category-detail-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-layer-group"></i>
            {categoryName} Category Details
          </Modal.Title>
          
          {/* Export Data Button removed */}
        </Modal.Header>
        <Modal.Body>
          {loading ? (
            <div className="text-center p-5">
              <Spinner animation="border" role="status" className="text-primary">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <p className="mt-3 text-muted">Loading category details...</p>
            </div>
          ) : error ? (
            <Alert variant="danger">{error}</Alert>
          ) : categoryDetails ? (
            <div className="category-details-container">
              <Card className="mb-4 border-0 shadow-sm">
                <Card.Body>
                  <Row className="g-4">
                    <Col md={3}>
                      <div className="detail-stat">
                        <h6>Total Brands</h6>
                        <h3>{categoryDetails.brands.length}</h3>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="detail-stat">
                        <h6>Total Accounts</h6>
                        <h3>{totals.totalAccounts}</h3>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="detail-stat">
                        <h6>Total Posts</h6>
                        <h3>{totals.totalPosts.toLocaleString()}</h3>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="detail-stat">
                        <h6>Total Comments</h6>
                        <h3>{totals.totalComments.toLocaleString()}</h3>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
              
              <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mb-4 category-tabs"
                fill
              >
                <Tab eventKey="overview" title={<><i className="fas fa-info-circle me-2"></i>Overview</>}>
                  <Accordion defaultActiveKey="0" className="brands-accordion">
                    {categoryDetails.brands.map((brand, index) => (
                      <Accordion.Item eventKey={String(index)} key={brand.brand_id}>
                        <Accordion.Header>
                          <div className="d-flex justify-content-between w-100 me-4 align-items-center">
                            <div>
                              <i className={`fas fa-building me-2 ${brand.is_outdated ? 'text-danger' : 'text-success'}`}></i>
                              {brand.brand_name}
                            </div>
                            <div>
                              <Badge bg="primary" className="me-2">{brand.totalaccounts} Accounts</Badge>
                              {brand.is_outdated ? (
                                <Badge bg="danger">Outdated</Badge>
                              ) : (
                                <Badge bg="success">Up to date</Badge>
                              )}
                            </div>
                          </div>
                        </Accordion.Header>
                        <Accordion.Body>
                          {/* ... existing brand details code ... */}
                          <div className="brand-details">
                            <Row className="mb-3 g-4">
                              <Col md={6}>
                                <Card>
                                  <Card.Body>
                                    <h6><i className="fas fa-chart-bar"></i> Engagement Metrics</h6>
                                    <div className="posts-comments-container">
                                      <div className="stat-circle">
                                        <h5>{brand.brandpost.toLocaleString()}</h5>
                                        <small>Posts</small>
                                      </div>
                                      <div className="stat-circle">
                                        <h5>{brand.comments.toLocaleString()}</h5>
                                        <small>Comments</small>
                                      </div>
                                    </div>
                                  </Card.Body>
                                </Card>
                              </Col>
                              <Col md={6}>
                                <Card>
                                  <Card.Body>
                                    <h6><i className="fas fa-calendar-alt"></i> Data Collection Period</h6>
                                    <div className="category-timeline">
                                      <div className="timeline-dates">
                                        <div className="date-display">
                                          <span className="date-label">First Collected</span>
                                          <span className="date-value">{formatDate(brand.first_datafetchedinduration)}</span>
                                        </div>
                                        
                                        <i className="fas fa-arrow-right"></i>
                                        
                                        <div className="date-display">
                                          <span className="date-label">Last Updated</span>
                                          <span className="date-value">{formatDate(brand.last_datafetchedinduration)}</span>
                                        </div>
                                      </div>
                                    </div>
                                    {brand.is_outdated && (
                                      <div className="alert-outdated">
                                        <i className="fas fa-exclamation-triangle"></i>
                                        <span>This brand has outdated data that needs refresh</span>
                                      </div>
                                    )}
                                  </Card.Body>
                                </Card>
                              </Col>
                            </Row>
                            <div className="text-end">
                              <Button 
                                variant="primary" 
                                className="view-accounts-btn"
                                onClick={() => handleBrandClick(brand)}
                              >
                                <i className="fas fa-eye me-2"></i> View {brand.totalaccounts} Accounts
                              </Button>
                            </div>
                          </div>
                        </Accordion.Body>
                      </Accordion.Item>
                    ))}
                  </Accordion>
                </Tab>
                <Tab eventKey="charts" title={<><i className="fas fa-chart-bar me-2"></i>Analytics</>}>
                  {renderCharts()}
                </Tab>
              </Tabs>
            </div>
          ) : (
            <Alert variant="info">No details available for this category.</Alert>
          )}
        </Modal.Body>
      </Modal>

      {/* Brand Detail Modal */}
      {selectedBrand && (
        <BrandDetail
          show={showBrandDetail}
          onHide={handleCloseBrandDetail}
          categoryId={categoryId}
          brandId={selectedBrand.brand_id}
          categoryName={categoryName}
          brandName={selectedBrand.brand_name}
        />
      )}
    </>
  );
};

export default CategoryDetail;
